import React, { useMemo, useState } from 'react'
import ScheduleAdminUnitsList from './ScheduleAdminUnitsList'
import { NotFill } from './NotFill'
import {
  useScheduleFiltersVM,
  type ScheduleFiltersVMProps,
  STATUS_PHASES,
  VIEWING_PHASES,
  ADS_PHASES
} from './useScheduleFiltersVM'

import './ScheduleFiltersListDesktop.scss'

interface ScheduleFiltersListDesktopProps extends ScheduleFiltersVMProps {
  filteredViewings: any
  filteredOffers: any
  filteredAdvertises: any
}

type UnitCategoryFilter = 'all' | 'active' | 'sold' | 'archive'

export default function ScheduleFiltersListDesktop({
  scheduleFiltersApply,
  setScheduleFiltersApply
}: ScheduleFiltersListDesktopProps): JSX.Element {
  const [unitSearch, setUnitSearch] = useState('')
  const [unitCategory, setUnitCategory] = useState<UnitCategoryFilter>('all')

  const {
    loading,
    filterNavigate,
    localFilters,
    setLocalFilters,
    listUnits,
    applyFilters,
    resetFilters,
    closePopup
  } = useScheduleFiltersVM({ scheduleFiltersApply, setScheduleFiltersApply })

  const normalizedUnitSearch = unitSearch.trim().toLowerCase()

  const filteredUnits = useMemo(() => {
    return (listUnits ?? []).filter((unit) => {
      const statuses = Array.isArray(unit?.attributes?.Statuses) ? unit.attributes.Statuses.map((status) => String(status).toLowerCase()) : []
      const isArchived = statuses.includes('archived')
      const isSold = statuses.some((status) => status.includes('sold') || status.includes('off market'))
      const isActive = !isArchived && !isSold

      const isCategoryMatch = unitCategory === 'all'
        || (unitCategory === 'archive' && isArchived)
        || (unitCategory === 'sold' && isSold)
        || (unitCategory === 'active' && isActive)

      if (!isCategoryMatch) {
        return false
      }

      if (normalizedUnitSearch === '') {
        return true
      }

      const headline = String(unit?.attributes?.Marketing?.Headline ?? '').toLowerCase()
      const area = String(unit?.attributes?.Property?.data?.attributes?.Area ?? '').toLowerCase()
      const propertyName = String(unit?.attributes?.Property?.data?.attributes?.Name ?? '').toLowerCase()
      const unitNumber = String(unit?.attributes?.Number ?? '').toLowerCase()

      return (
        headline.includes(normalizedUnitSearch)
        || area.includes(normalizedUnitSearch)
        || propertyName.includes(normalizedUnitSearch)
        || unitNumber.includes(normalizedUnitSearch)
      )
    })
  }, [listUnits, normalizedUnitSearch, unitCategory])

  return (
    <div className="schedule-filters-desktop__panel">
      <div className="schedule-filters-desktop__header">
        <div>
          <p className="schedule-filters-desktop__eyebrow">Filters</p>
          <h3 className="schedule-filters-desktop__title">Schedule filters</h3>
          <p className="schedule-filters-desktop__subtitle">
            Refine tasks by type, phase, or a specific unit.
          </p>
        </div>

        <button
          type="button"
          className="schedule-filters-desktop__close"
          aria-label="Close filters"
          onClick={closePopup}
        >
          ×
        </button>
      </div>

      <div className="schedule-filters-desktop__content">
        <div className="schedule-filters-desktop__layout">
          <section className="schedule-filters-desktop__section schedule-filters-desktop__section--task-type">
            <div className="schedule-filters-desktop__section-head">
              <div>
                <p className="schedule-filters-desktop__section-eyebrow">Units</p>
                <h5 className="schedule-filters-desktop__section-title">Task type</h5>
              </div>
            </div>

            <NotFill
              categories={[
                { name: 'All', value: 'all', items: [] },
                { name: 'Sale', value: 'sale', items: [] },
                { name: 'Rent', value: 'rent', items: [] }
              ]}
              selectedCategory={localFilters.sortByTypeUnit}
              onChange={(sortByTypeUnit) => {
                setLocalFilters((prev) => ({ ...prev, sortByTypeUnit }))
              }}
            />
          </section>

          <section className="schedule-filters-desktop__section schedule-filters-desktop__section--units">
            <div className="schedule-filters-desktop__section-head">
              <div>
                <p className="schedule-filters-desktop__section-eyebrow">Unit</p>
                <h5 className="schedule-filters-desktop__section-title">Filter by unit</h5>
              </div>
            </div>

            <div className="schedule-filters-desktop__unit-tools">
              <input
                type="text"
                className="schedule-filters-desktop__unit-search"
                placeholder="Search by unit number, headline, area..."
                value={unitSearch}
                onChange={(event) => {
                  setUnitSearch(event.target.value)
                }}
              />

              <div className="schedule-filters-desktop__unit-categories">
                <button
                  type="button"
                  className={`schedule-filters-desktop__unit-category ${unitCategory === 'all' ? 'is-active' : ''}`}
                  onClick={() => {
                    setUnitCategory('all')
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`schedule-filters-desktop__unit-category ${unitCategory === 'active' ? 'is-active' : ''}`}
                  onClick={() => {
                    setUnitCategory('active')
                  }}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`schedule-filters-desktop__unit-category ${unitCategory === 'sold' ? 'is-active' : ''}`}
                  onClick={() => {
                    setUnitCategory('sold')
                  }}
                >
                  Sold / Off market
                </button>
                <button
                  type="button"
                  className={`schedule-filters-desktop__unit-category ${unitCategory === 'archive' ? 'is-active' : ''}`}
                  onClick={() => {
                    setUnitCategory('archive')
                  }}
                >
                  Archive
                </button>
              </div>
            </div>

            <div className="schedule-filters-desktop__units-scroll">
              <ScheduleAdminUnitsList
                units={filteredUnits}
                selectedCategory={String(localFilters.unitID)}
                onChange={(unitID) => {
                  setLocalFilters((prev) => ({ ...prev, unitID: Number(unitID) || 0 }))
                }}
              />

              {filteredUnits.length === 0 && (
                <div className="schedule-filters-desktop__empty">
                  No units matched your category and search.
                </div>
              )}
            </div>
          </section>

          {filterNavigate === 'offers' && (
            <section className="schedule-filters-desktop__section schedule-filters-desktop__section--phases">
              <div className="schedule-filters-desktop__section-head">
                <div>
                  <p className="schedule-filters-desktop__section-eyebrow">Offers</p>
                  <h5 className="schedule-filters-desktop__section-title">Phases</h5>
                </div>
              </div>

              <NotFill
                categories={STATUS_PHASES}
                selectedCategory={localFilters.byOfferGroups}
                onChange={(byOfferGroups) => {
                  setLocalFilters((prev) => ({ ...prev, byOfferGroups }))
                }}
              />
            </section>
          )}

          {filterNavigate === 'viewings' && (
            <section className="schedule-filters-desktop__section schedule-filters-desktop__section--phases">
              <div className="schedule-filters-desktop__section-head">
                <div>
                  <p className="schedule-filters-desktop__section-eyebrow">Viewings</p>
                  <h5 className="schedule-filters-desktop__section-title">Phases</h5>
                </div>
              </div>

              <NotFill
                categories={VIEWING_PHASES}
                selectedCategory={localFilters.byViewingGroups}
                onChange={(byViewingGroups) => {
                  setLocalFilters((prev) => ({ ...prev, byViewingGroups }))
                }}
              />
            </section>
          )}

          {filterNavigate === 'adverts' && (
            <section className="schedule-filters-desktop__section schedule-filters-desktop__section--phases">
              <div className="schedule-filters-desktop__section-head">
                <div>
                  <p className="schedule-filters-desktop__section-eyebrow">Advertisements</p>
                  <h5 className="schedule-filters-desktop__section-title">Phases</h5>
                </div>
              </div>

              <NotFill
                categories={ADS_PHASES}
                selectedCategory={localFilters.byAddsGroups}
                onChange={(byAddsGroups) => {
                  setLocalFilters((prev) => ({ ...prev, byAddsGroups }))
                }}
              />
            </section>
          )}
        </div>
      </div>

      <div className="schedule-filters-desktop__footer">
        <button
          type="button"
          className="schedule-filters-desktop__reset"
          disabled={loading}
          onClick={resetFilters}
        >
          Reset
        </button>

        <button
          type="button"
          className="schedule-filters-desktop__apply btn btn-bso"
          disabled={loading}
          onClick={applyFilters}
        >
          {loading ? 'Please wait...' : 'Apply filters'}
        </button>
      </div>
    </div>
  )
}
